import { Link } from 'react-router-dom';
// Import lucide-react icon
import { ArrowRight, Sparkles, BarChart3, Zap, Users } from 'lucide-react';
// Import shadcn/ui components
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export function HeroSection() {
  return (
    // Use theme background color (e.g., background or a subtle variant)
    <div className="relative bg-background overflow-hidden">
      {/* Enhanced Subtle Animated Background */}
      <div className="absolute inset-0 -z-10 overflow-hidden blur-3xl" aria-hidden="true">
        <div 
          className="absolute left-[max(50%,16rem)] top-[-10rem] h-[64rem] w-[128rem] -translate-x-1/2 transform-gpu"
          style={{
            background: 'radial-gradient(circle at center, rgba(var(--primary-rgb), 0.15) 0%, transparent 50%)',
            maskImage: 'radial-gradient(closest-side, white 60%, transparent)'
          }}
        />
      </div>
      {/* Optional: Add a secondary subtle gradient */}
      <div className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-background" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        {/* Increased vertical padding */}
        <div className="py-24 sm:py-32 lg:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-20 items-center">
            {/* Text Content Area */}
            <div className="text-center lg:text-left">
              {/* Use shadcn Badge with primary variant */}
              <Badge variant="outline" className="mb-6 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 px-4 py-1.5 font-medium">
                <Sparkles className="mr-2 h-4 w-4 text-primary/80" />
                Complete hiring platform trusted by 500+ companies
              </Badge>

              {/* Main Heading - Use theme text colors */} 
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground">
                <span className="block">Complete <span className="text-primary">Hiring Platform</span></span>
                <span className="block">Powered by AI</span>
              </h1>
              {/* Description - Use muted foreground color */}
              <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0">
                Transform your entire hiring process with our comprehensive platform. From project management to market intelligence, AI-powered resume analysis to team collaboration—everything you need to hire the best talent faster.
              </p>

              {/* Social proof metrics */}
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-xl mx-auto lg:mx-0">
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/10">
                  <div className="flex items-center justify-center lg:justify-start mb-1">
                    <BarChart3 className="h-4 w-4 text-primary mr-1" />
                    <span className="text-2xl font-bold text-primary">82%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Improved hire quality</p>
                </div>
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/10">
                  <div className="flex items-center justify-center lg:justify-start mb-1">
                    <Zap className="h-4 w-4 text-primary mr-1" />
                    <span className="text-2xl font-bold text-primary">70%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Faster time-to-hire</p>
                </div>
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/10">
                  <div className="flex items-center justify-center lg:justify-start mb-1">
                    <Users className="h-4 w-4 text-primary mr-1" />
                    <span className="text-2xl font-bold text-primary">45%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Reduced turnover</p>
                </div>
              </div>

              {/* Feature bullets */}
              <div className="mt-8 space-y-3 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto lg:mx-0">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-2"><span className="font-medium">Complete Hiring Dashboard:</span> Manage projects, track candidates, and analyze trends</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-2"><span className="font-medium">AI-Powered Resume Analysis:</span> Advanced matching with detailed scoring and insights</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-2"><span className="font-medium">Market Intelligence:</span> Salary insights, talent trends, and competitive analysis</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-2"><span className="font-medium">Team Collaboration:</span> Share insights, collaborate on decisions, and track progress</p>
                </div>
              </div>

              {/* CTA Button - Use shadcn Button */}
              <div className="mt-10 flex flex-col items-center lg:items-start">
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                  <Button asChild size="lg" className="group shadow-lg hover:shadow-xl transition-shadow w-full sm:w-auto">
                    <a href="mailto:sales@topmatchtalent.com?subject=Request Demo - Complete Hiring Platform">
                      Request Demo
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                    <Link to="#how-it-works" onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('how-it-works');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}>
                      See How It Works
                    </Link>
                  </Button>
                </div>
                <div className="mt-3 flex items-center text-xs text-muted-foreground">
                  <svg className="h-4 w-4 text-green-500 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Enterprise-grade platform • Custom setup • Professional onboarding</span>
                </div>
              </div>
            </div>

            {/* Enhanced Visual Area - Animated product preview */}
            <div className="relative w-full max-w-lg mx-auto lg:max-w-none lg:mx-0 h-[450px] sm:h-[500px]">
                <div className="absolute inset-0 flex items-center justify-center">
                    {/* Analysis Card */}
                    <div 
                        className="absolute w-64 h-96 sm:w-72 sm:h-[400px] bg-card/90 backdrop-blur-sm rounded-xl shadow-2xl border border-border/60 overflow-hidden transform rotate-2 translate-x-10 translate-y-8 animate-subtle-float"
                    >
                       <div className="h-full p-6 flex flex-col">
                           <div className="flex items-center space-x-2 mb-4">
                               <div className="h-7 w-7 rounded bg-primary/10 flex items-center justify-center">
                                   <BarChart3 className="h-4 w-4 text-primary" />
                               </div>
                               <div className="text-lg font-semibold">Candidate Match</div>
                           </div>
                           <div className="bg-muted/50 rounded-lg p-3 mb-3">
                               <div className="flex justify-between mb-1">
                                   <span className="text-sm font-medium">Match Score</span>
                                   <span className="text-sm font-bold text-green-600">86%</span>
                               </div>
                               <div className="w-full bg-background rounded-full h-2">
                                   <div className="bg-green-500 h-2 rounded-full" style={{ width: "86%" }}></div>
                               </div>
                           </div>
                           <div className="space-y-2 flex-grow">
                               <div className="flex justify-between">
                                   <span className="text-xs">Technical Skills</span>
                                   <span className="text-xs font-medium text-primary">Strong</span>
                               </div>
                               <div className="flex justify-between">
                                   <span className="text-xs">Experience</span>
                                   <span className="text-xs font-medium text-primary">5+ years</span>
                               </div>
                               <div className="flex justify-between">
                                   <span className="text-xs">Interview Ready</span>
                                   <span className="text-xs font-medium text-green-600">Yes</span>
                               </div>
                           </div>
                           <div className="mt-2 pt-2 border-t border-border/50">
                               <button className="w-full bg-primary/10 text-primary text-sm py-1.5 rounded-md font-medium hover:bg-primary/20 transition-colors">
                                   View Details
                               </button>
                           </div>
                       </div>
                    </div>
                    
                    {/* Interview Plan Card */}
                    <div 
                        className="absolute w-64 h-96 sm:w-72 sm:h-[400px] bg-card/90 backdrop-blur-sm rounded-xl shadow-2xl border border-border/60 overflow-hidden transform -rotate-3 -translate-x-10 -translate-y-8 animate-subtle-float"
                        style={{ animationDelay: '1.2s' }}
                    >
                       <div className="h-full p-6 flex flex-col">
                           <div className="flex items-center space-x-2 mb-4">
                               <div className="h-7 w-7 rounded bg-primary/10 flex items-center justify-center">
                                   <Users className="h-4 w-4 text-primary" />
                               </div>
                               <div className="text-lg font-semibold">Interview Plan</div>
                           </div>
                           <div className="space-y-3 flex-grow">
                               <div className="bg-muted/50 p-3 rounded-lg">
                                   <div className="text-xs font-semibold mb-1">Technical Skills (15 min)</div>
                                   <div className="text-xs text-muted-foreground">Assess React, TypeScript experience</div>
                               </div>
                               <div className="bg-muted/50 p-3 rounded-lg">
                                   <div className="text-xs font-semibold mb-1">Problem Solving (20 min)</div>
                                   <div className="text-xs text-muted-foreground">Async workflow optimization</div>
                               </div>
                               <div className="bg-muted/50 p-3 rounded-lg">
                                   <div className="text-xs font-semibold mb-1">Team Collaboration (10 min)</div>
                                   <div className="text-xs text-muted-foreground">Cross-functional team experience</div>
                               </div>
                           </div>
                           <div className="mt-2 pt-2 border-t border-border/50">
                               <button className="w-full bg-primary/10 text-primary text-sm py-1.5 rounded-md font-medium hover:bg-primary/20 transition-colors">
                                   Print Interview Plan
                               </button>
                           </div>
                       </div>
                    </div>
                    
                    {/* Central element - Use primary color */} 
                    <div className="absolute w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center shadow-inner">
                        <div className="w-8 h-8 rounded-full bg-primary animate-pulse"></div>
                    </div>
                </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}